import jsPDF from 'jspdf';
import moment from 'moment';
import autoTable, { Cell, CellDef, CellInput, RowInput } from 'jspdf-autotable';
import { ServiceContainer } from '../ServiceContainer/ServiceContainer';

import { Filter } from '../../Enum/Filters';
import InterestUri from '../../Enum/InterestUri';
import { NewsType } from '../../Enum/Interest/NewsType';
import { CellDecorator, RowDecorator } from '../../Enum/TableDecorator';

import { HeaderCell, Row as RowInterface } from '../../Interface/Hook/Interest/InterestTableDTO';
import { Cell as CellInterface } from '../../Interface/Response/Interest/AcceptationGuideData';
import { Sort } from '../../Interface/Table/Sort';
import { Label } from '../../Interface/Response/Interest/Label';

import scopeTableData from '../ScopeTableData';
import sortTableData from '../SortTableData';
import { decorateCellContent } from './Pdf/CellToStringConverter';
import {
    cellStyle,
    decorateLegendaCell,
    getIconDimensions,
    headCellStyle,
    lineColor,
    loweredIconBase64,
    raiseIconBase64,
    scopedCellStyle,
    tableStyle,
    textColor,
    textSpacer,
} from './Pdf/Styles';
import { filterToPdfTableCell } from './Pdf/FilterToPdfTable';

const createRow = (row: RowInterface): CellInput[] =>
    row.cells.map(cell => {
        return row.decorator === RowDecorator.ScopeRow
            ? {
                  content: decorateCellContent(cell.value, cell.decorator, row.decorator),
                  colSpan: row.cells.length,
                  styles: scopedCellStyle,
              }
            : {
                  content: decorateCellContent(cell.value, cell.decorator),
                  styles: cell.decorator === CellDecorator.CompanyIcon ? { ...cellStyle, cellWidth: 10 } : cellStyle,
              };
    });

const getCellPaddingLeft = (cell: Cell): number => {
    return typeof cell.styles.cellPadding === 'number' ? cell.styles.cellPadding : 0;
};

const getLabel = (finDataCode: string): Label | undefined => {
    return ServiceContainer.storeGateway
        .getStore()
        .getState()
        .label.labels.find(label => label.findataCode === finDataCode);
};

const rawCellIsCellDef = (rawCell: any): rawCell is CellDef =>
    typeof rawCell === 'object' && rawCell.hasOwnProperty('content');

const getHeaderText = (section: InterestUri): string => {
    switch (section) {
        case InterestUri.FixedPeriod:
        case InterestUri.MarketValue:
            return 'Rente overzicht';
        case InterestUri.BridgingLoan:
            return 'Overbruggingskrediet overzicht';
        default:
            return 'Vergelijken labeloverzicht';
    }
};

const drawHeader = (pdfDoc: jsPDF, section: InterestUri): void => {
    pdfDoc.setTextColor(textColor);
    pdfDoc.setFontSize(20);
    pdfDoc.text(getHeaderText(section), 14, 15);

    pdfDoc.setFontSize(8);
    const resultFetchedAt = `Resultaat op ${moment(ServiceContainer.storeGateway.getState().filter.fetchedAt).format(
        'DD/MM/YYYY - H:mm',
    )}`;

    pdfDoc.text(resultFetchedAt, pdfDoc.internal.pageSize.width - 14 - pdfDoc.getTextWidth(resultFetchedAt), 15);

    pdfDoc.setDrawColor(lineColor);
    pdfDoc.line(14, 19, pdfDoc.internal.pageSize.width - 14, 19);
};

const drawLegenda = (pdfDoc: jsPDF, section: InterestUri, filterSettings?: Filter[]): void => {
    if (filterSettings === undefined) {
        return;
    }

    if (section !== InterestUri.MarketValue && section !== InterestUri.FixedPeriod) {
        return;
    }

    const head: RowInput[] = [
        [
            { ...decorateLegendaCell('Uitgangspunten overzicht'), colSpan: 2 },
            { ...decorateLegendaCell(`${textSpacer}Legenda`, true) },
        ],
    ];

    const body = filterSettings.reduce((acc, current) => {
        const rowInput = filterToPdfTableCell(current);

        if (rowInput !== undefined) {
            acc.push(rowInput);
        }

        return acc;
    }, [] as RowInput[]);

    body.forEach((value, index) => {
        switch (index) {
            case 0:
                (value as CellDef[]).push(
                    decorateLegendaCell(
                        `${textSpacer}1,23%: Doorgestreepte tarieven zijn niet beschikbaar voor nieuwe klanten`,
                        true,
                    ),
                );
                break;
            case 1:
                (value as CellDef[]).push(
                    decorateLegendaCell(`${textSpacer + textSpacer}: Rentedaling is aangekondigd`, true),
                );
                break;
            case 2:
                (value as CellDef[]).push(
                    decorateLegendaCell(`${textSpacer + textSpacer}: Rentestijging is aangekondigd`, true),
                );
                break;
            default:
                (value as CellDef[]).push(decorateLegendaCell('', true));
                break;
        }
    });

    autoTable(pdfDoc, {
        head,
        body,
        theme: 'plain',
        styles: tableStyle,
        startY: 25,
        didDrawCell: data => {
            if ((data.cell.raw as CellDef).content?.toString().indexOf('1,23%') !== -1) {
                pdfDoc.setDrawColor(textColor);
                pdfDoc.line(
                    data.cell.x + getCellPaddingLeft(data.cell) + pdfDoc.getTextWidth(textSpacer),
                    data.cell.y + data.cell.height / 2,
                    data.cell.x + getCellPaddingLeft(data.cell) + pdfDoc.getTextWidth(`${textSpacer}1,23%`),
                    data.cell.y + data.cell.height / 2,
                );
            }
            if ((data.cell.raw as CellDef).content?.toString().indexOf('Rentestijging') !== -1) {
                pdfDoc.addImage(raiseIconBase64, data.cell.x + 4.5, data.cell.y + 0.5, 2.5, 2.5);
            }
            if ((data.cell.raw as CellDef).content?.toString().indexOf('Rentedaling') !== -1) {
                pdfDoc.addImage(loweredIconBase64, data.cell.x + 4.5, data.cell.y + 0.5, 2.5, 2.5);
            }
        },
    });
};

const drawFooters = (pdfDoc: jsPDF): void => {
    for (let i = 1; i <= pdfDoc.getNumberOfPages(); i++) {
        pdfDoc.setPage(i);
        pdfDoc.setTextColor(textColor);
        pdfDoc.setFontSize(8);
        const disclaimer = 'Aan dit overzicht kunnen geen rechten worden ontleend.';
        pdfDoc.text(`${i}/${pdfDoc.getNumberOfPages()}`, 15, pdfDoc.internal.pageSize.height - 10);
        pdfDoc.text(
            disclaimer,
            pdfDoc.internal.pageSize.width - (pdfDoc.getTextWidth(disclaimer) + 15),
            pdfDoc.internal.pageSize.height - 10,
        );

        pdfDoc.setDrawColor(lineColor);
        pdfDoc.line(
            15,
            pdfDoc.internal.pageSize.height - 18,
            pdfDoc.internal.pageSize.width - 15,
            pdfDoc.internal.pageSize.height - 18,
        );
    }
};

const getOrientation = (headers: HeaderCell[], section: InterestUri): 'landscape' | 'portrait' => {
    const maxAmountColumns = section === InterestUri.MarketValue || section === InterestUri.FixedPeriod ? 5 : 3;
    return headers.length > maxAmountColumns ? 'landscape' : 'portrait';
};

const getValueFromHeader = (header: HeaderCell): string => {
    if (header.decorator !== CellDecorator.CompanyIcon) {
        return header.value as string;
    }

    const value = header.appendix;
    if (typeof value === 'string') {
        return value;
    }

    return (value?.value as CellInterface).label;
};

export const makePdf = (
    rows: RowInterface[],
    headers: HeaderCell[],
    section: InterestUri,
    scopable?: boolean,
    sort?: Sort,
    filterSettings?: Filter[],
): Blob => {
    // noinspection JSPotentiallyInvalidConstructorUsage
    const pdfDoc = new jsPDF({
        orientation: getOrientation(headers, section),
    });

    drawHeader(pdfDoc, section);

    drawLegenda(pdfDoc, section, filterSettings);

    autoTable(pdfDoc, {
        head: [
            headers.map(header => ({
                content: decorateCellContent(getValueFromHeader(header).toUpperCase()),
                styles: headCellStyle,
            })),
        ],
        body: scopable
            ? scopeTableData(rows).map(row => createRow(row))
            : sortTableData(rows, headers, sort).map(row => createRow(row)),

        didParseCell: data => {
            if (data.cell.text[0].indexOf(`[${CellDecorator.Strikethrough}]`) >= 0) {
                data.cell.text = [
                    decorateCellContent(
                        data.cell.text[0].replace(`[${CellDecorator.Strikethrough}]`, ''),
                        CellDecorator.Interest,
                    ),
                ];
                return;
            }
            if (data.cell.text[0].indexOf(`[${CellDecorator.CompanyIcon}]`) >= 0) {
                data.cell.text = [textSpacer];
                return;
            }
            if (data.cell.text[0].indexOf(`[${RowDecorator.ScopeRow}]`) >= 0) {
                data.cell.text = [data.cell.text[0].replace(`[${RowDecorator.ScopeRow}]`, '')];
            }
            if (data.cell.text[0].indexOf(`[${NewsType.UPPED}]`) >= 0) {
                data.cell.text = [data.cell.text[0].replace(`[${NewsType.UPPED}]`, '') + '   '];
            }
            if (data.cell.text[0].indexOf(`[${NewsType.LOWERED}]`) >= 0) {
                data.cell.text = [data.cell.text[0].replace(`[${NewsType.LOWERED}]`, '') + '   '];
            }
        },
        didDrawCell: data => {
            if (rawCellIsCellDef(data.cell.raw) && typeof data.cell.raw.content === 'string') {
                if (data.cell.raw.content.indexOf(`[${CellDecorator.Strikethrough}]`) >= 0) {
                    pdfDoc.setDrawColor(textColor);
                    pdfDoc.line(
                        data.cell.x + getCellPaddingLeft(data.cell),
                        data.cell.y + data.cell.height / 2,
                        data.cell.x + getCellPaddingLeft(data.cell) + pdfDoc.getTextWidth(data.cell.text[0]),
                        data.cell.y + data.cell.height / 2,
                    );
                }
                if (data.cell.raw.content.indexOf(`[${CellDecorator.CompanyIcon}]`) >= 0) {
                    const finDataCode = data.cell.raw.content.replace(`[${CellDecorator.CompanyIcon}]`, '');

                    const label = getLabel(finDataCode.toLowerCase());
                    if (label && label.base64Icon !== undefined) {
                        const dimensions = getIconDimensions(label);
                        pdfDoc.addImage(
                            label.base64Icon,
                            data.cell.x + (data.cell.width - dimensions.width) / 2,
                            data.cell.y + (data.cell.height - dimensions.height) / 2,
                            dimensions.width,
                            dimensions.height,
                        );
                    }
                }
                if (data.cell.raw.content.indexOf(`[${NewsType.LOWERED}]`) >= 0) {
                    pdfDoc.addImage(loweredIconBase64, data.cell.x + 11.5, data.cell.y + 2.5, 2.75, 2.75);
                }
                if (data.cell.raw.content.indexOf(`[${NewsType.UPPED}]`) >= 0) {
                    pdfDoc.addImage(raiseIconBase64, data.cell.x + 11.5, data.cell.y + 2.5, 2.75, 2.75);
                }
            }
        },
        styles: tableStyle,
        theme: 'plain',
        margin: { bottom: 22 },
        startY: filterSettings === undefined ? 25 : undefined,
    });

    drawFooters(pdfDoc);

    return pdfDoc.output('blob');
};
